/**
 * Main-thread client for the photo encode worker (photoEncoder.worker.js), plus the main-thread
 * fallback that has to behave identically when the worker is unavailable or dies.
 *
 * The component talks to this module, never to a Worker directly: that is what makes the encode
 * path mockable in vitest/happy-dom, which has neither Worker nor OffscreenCanvas.
 *
 * Contract per capture (see the worker for the wire protocol):
 *
 *   const job = encoder.submit({ bitmap, ... });
 *   const { isBlack } = await job.verdict;   // ~ms: fire the shutter here, or retry
 *   const { blob }    = await job.blob;      // resolves null when the verdict was black
 *
 * Both promises reject if the worker fails or goes quiet; the caller is expected to redo the
 * capture through the main thread when that happens.
 */
import { encodeWithCap } from './photoEncoderCore';

// Hard deadline on a single job. Matched to the capture pipeline's own 10s safety timeout: past
// that point the UI has already given up on this capture, so a worker that is still thinking is
// indistinguishable from a dead one — and a wedged worker must not leave the encode path hanging
// forever, it must hand the shot back to toBlob.
const JOB_TIMEOUT_MS = 10000;

// Consecutive per-job failures before the whole path is given up on. A single failure is worth
// retrying (a transient allocation failure under memory pressure); a run of them means this
// browser's worker cannot do the job at all — no 2D context in a worker, no JPEG in
// convertToBlob — and every further capture would pay a freeze, a round trip and a re-encode on
// the main thread just to learn that again.
const MAX_CONSECUTIVE_JOB_FAILURES = 3;

/**
 * True when this browser can run the worker path at all. OffscreenCanvas is the real gate —
 * everything else (Worker, createImageBitmap) has been available far longer.
 */
export function isWorkerEncodeSupported() {
  return typeof Worker !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined' &&
    typeof createImageBitmap === 'function';
}

/**
 * Encodes a DOM canvas with the same size-cap ladder the worker uses. This is the fallback path,
 * and the reason encodeWithCap lives in photoEncoderCore: a cap that only holds on one of the two
 * paths is a cap that silently stops holding.
 */
export function encodeCanvasWithCap(canvas, options) {
  const adapter = {
    convertToBlob: ({ type, quality }) => new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), type, quality);
    }),
  };
  return encodeWithCap(adapter, options);
}

/**
 * Creates the encoder client. Constructing it constructs the Worker, so call it once (on
 * cameraReady), not per capture: worker startup is ~10-30ms of module fetch + parse that has no
 * business sitting between a tap and a shutter.
 *
 * `onFailure` is called once, with an Error, the first time the worker breaks in a way that takes
 * the whole path down (construction, `error`, `messageerror`). After that `isAvailable()` is false
 * and every submit throws, so the component routes to the main thread without retrying a corpse.
 */
export function createPhotoEncoder({ onFailure } = {}) {
  let worker = null;
  let available = false;
  let ready = false;
  let consecutiveFailures = 0;
  let nextId = 1;
  const pending = new Map();

  const failAll = (error) => {
    for (const entry of pending.values()) {
      clearTimeout(entry.timer);
      entry.rejectVerdict(error);
      entry.rejectBlob(error);
    }
    pending.clear();
  };

  const die = (error) => {
    const wasAvailable = available;
    available = false;
    ready = false;
    failAll(error);
    try {
      worker?.terminate();
    } catch (e) {
      // Terminating an already-dead worker is not an error worth propagating.
    }
    worker = null;
    if (wasAvailable && onFailure) onFailure(error);
  };

  const settle = (id) => {
    const entry = pending.get(id);
    if (entry) {
      clearTimeout(entry.timer);
      pending.delete(id);
    }
    return entry;
  };

  try {
    if (!isWorkerEncodeSupported()) throw new Error('OffscreenCanvas/Worker unavailable');
    // Webpack 5 (Vue CLI 5) understands this form natively and emits the worker as its own chunk —
    // no worker-loader, no new dependency.
    worker = new Worker(new URL('./photoEncoder.worker.js', import.meta.url));
    available = true;
  } catch (error) {
    available = false;
    if (onFailure) onFailure(error);
  }

  if (worker) {
    worker.onerror = (event) => die(new Error(`photo encode worker error: ${event?.message || 'unknown'}`));
    worker.onmessageerror = () => die(new Error('photo encode worker could not deserialize a message'));
    worker.onmessage = (event) => {
      const msg = event.data;
      if (!msg) return;
      if (msg.type === 'ready') {
        // The worker has loaded and its globals resolved. Until this lands, captures stay on the
        // main thread: constructing a Worker is synchronous but FETCHING its chunk is not, and on
        // a restaurant's network the first shutter after opening the camera must not wait on it.
        ready = true;
        return;
      }
      const entry = pending.get(msg.id);
      if (!entry) return; // a late answer to a job we already gave up on
      if (msg.type === 'verdict') {
        entry.resolveVerdict({
          isBlack: msg.isBlack, width: msg.width, height: msg.height,
          verdictMs: msg.verdictMs, decodeMs: msg.decodeMs,
        });
        if (msg.isBlack) {
          // A black frame is never encoded; resolve the blob side so the caller can drop the job
          // without leaving a promise dangling.
          settle(msg.id);
          entry.resolveBlob({ blob: null, isBlack: true });
        }
        return;
      }
      if (msg.type === 'encoded') {
        settle(msg.id);
        consecutiveFailures = 0;
        entry.resolveBlob({
          blob: msg.blob,
          isBlack: false,
          width: msg.width,
          height: msg.height,
          quality: msg.quality,
          steps: msg.steps,
          originalSize: msg.originalSize,
          overCap: msg.overCap,
          // How long the worker itself took. The gap between this and the main thread's
          // round-trip is how long the answer waited for a busy main thread.
          drawMs: msg.drawMs,
          encodeMs: msg.encodeMs,
          decodeMs: msg.decodeMs,
        });
        return;
      }
      if (msg.type === 'failed') {
        settle(msg.id);
        const error = new Error(`photo encode worker job failed: ${msg.message}`);
        // The caller acts on these: an undecodable sensor blob is retried with explicit photo
        // settings, a portrait still falls back to canvas. Neither says anything is wrong with
        // the worker, so they are reported but never counted towards giving up on it.
        error.reason = msg.reason || null;
        error.width = msg.width;
        error.height = msg.height;
        entry.rejectVerdict(error);
        entry.rejectBlob(error);
        if (msg.reason === 'decode' || msg.reason === 'portrait') return;
        if (++consecutiveFailures >= MAX_CONSECUTIVE_JOB_FAILURES) {
          die(new Error(`photo encode worker failed ${consecutiveFailures} jobs in a row: ${msg.message}`));
        }
      }
    };
  }

  return {
    /** The worker exists and has not died. Enough to queue work; not enough to route a capture. */
    isAvailable: () => available,

    /**
     * The worker has answered at least once, so its chunk is fetched, parsed and running. This —
     * not isAvailable() — is what decides whether a capture takes the worker route.
     */
    isReady: () => available && ready,

    /**
     * Hands a frozen frame to the worker. The ImageBitmap is TRANSFERRED, not copied — it belongs
     * to the worker from here on and must not be touched (or closed) by the caller.
     *
     * `timeout: false` submits without arming the deadline. Used for the warm-up frame, which is
     * queued before the worker has necessarily finished loading: on a slow network its answer can
     * legitimately be seconds away, and killing the encoder over that would disable the whole
     * path for the rest of the visit — over a frame nobody is waiting for.
     */
    submit(job, { timeout = true } = {}) {
      if (!available || !worker) throw new Error('photo encode worker unavailable');
      const id = nextId++;
      let resolveVerdict, rejectVerdict, resolveBlob, rejectBlob;
      const verdict = new Promise((res, rej) => { resolveVerdict = res; rejectVerdict = rej; });
      const blob = new Promise((res, rej) => { resolveBlob = res; rejectBlob = rej; });
      // Both promises are routinely ignored by the caller (a black retry never awaits the blob),
      // and an ignored rejection is an unhandled rejection. Attaching a no-op handler here marks
      // them as handled without changing what the caller receives.
      verdict.catch(() => {});
      blob.catch(() => {});

      const timer = timeout ? setTimeout(() => {
        // No answer within the deadline: treat the whole worker as gone, not just this job. A
        // worker that missed one capture will miss the next one too, and the operator should not
        // pay a 10s stall per photo to find that out.
        die(new Error(`photo encode worker timed out after ${JOB_TIMEOUT_MS}ms`));
      }, JOB_TIMEOUT_MS) : null;

      pending.set(id, { resolveVerdict, rejectVerdict, resolveBlob, rejectBlob, timer });
      try {
        // A bitmap is TRANSFERRED (moving it costs nothing, copying it costs a full frame); a Blob
        // is not transferable and does not need to be — structured clone passes it by reference.
        worker.postMessage({ ...job, id }, job.bitmap ? [job.bitmap] : []);
      } catch (error) {
        settle(id);
        die(error);
        throw error;
      }
      return { id, verdict, blob };
    },

    terminate() {
      available = false;
      ready = false;
      failAll(new Error('photo encode worker terminated'));
      try {
        worker?.terminate();
      } catch (e) {
        // Nothing to do on teardown.
      }
      worker = null;
    },
  };
}
